import React, { useEffect, useState } from 'react';
import { Plus, Sparkles, Zap, Target } from 'lucide-react';
import HourglassLoader from './ui/HourglassLoader';
import apiClient from '../lib/apiClient';
import { energyService } from '../services';
import './Campaigns.css';

function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [energy, setEnergy] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    objective: 'engagement',
    platform: 'instagram',
    duration_days: 7,
    target_audience: ''
  });

  const CAMPAIGN_COST = 50;

  const loadData = async () => {
    setLoading(true);
    try {
      const [campaignsData, energyData] = await Promise.all([
        apiClient.request('/campaigns'),
        energyService.getEnergy()
      ]);
      if (campaignsData?.success) setCampaigns(campaignsData.campaigns || []);
      if (energyData?.success) setEnergy(energyData.energy || 0);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const generateCampaign = async () => {
    if (energy < CAMPAIGN_COST) {
      alert(`Energia insuficiente. Necessário: ${CAMPAIGN_COST}`);
      return;
    }

    setGenerating(true);
    try {
      const data = await apiClient.request('/campaigns/generate', {
        method: 'POST',
        body: JSON.stringify(form)
      });

      if (!data?.success) {
        alert(data?.error || 'Falha ao gerar campanha');
        return;
      }

      setCampaigns((prev) => [data.campaign, ...prev]);
      setEnergy((prev) => Math.max(0, prev - CAMPAIGN_COST));
      setShowForm(false);
    } catch (error) {
      console.error('Erro ao gerar campanha:', error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="ds-campaigns-wrapper">
      <div className="ds-page-header">
        <div className="header-actions">
          <div className="energy-badge">
            <Zap size={16} />
            <span>{energy} créditos</span>
          </div>
          <button className="btn btn-outline" onClick={() => setShowForm((prev) => !prev)}>
            <div className="blob1"></div>
            <div className="blob2"></div>
            <div className="inner">
              <Plus size={16} /> Nova Campanha
            </div>
          </button>
        </div>
      </div>

      {showForm && (
        <section className="campaign-modal card">
          <div className="modal-body">
            <div className="energy-cost-info">
              <span>Custo de geração:</span>
              <strong>{CAMPAIGN_COST} créditos</strong>
            </div>

            <div className="input-group">
              <label>Objetivo</label>
              <select
                value={form.objective}
                onChange={(event) => setForm((prev) => ({ ...prev, objective: event.target.value }))}
              >
                <option value="engagement">Engajamento</option>
                <option value="awareness">Reconhecimento</option>
                <option value="traffic">Tráfego</option>
                <option value="conversion">Conversão</option>
              </select>
            </div>

            <div className="input-row">
              <div className="input-group">
                <label>Plataforma</label>
                <select
                  value={form.platform}
                  onChange={(event) => setForm((prev) => ({ ...prev, platform: event.target.value }))}
                >
                  <option value="instagram">Instagram</option>
                  <option value="facebook">Facebook</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="twitter">Twitter/X</option>
                  <option value="all">Todas</option>
                </select>
              </div>
              <div className="input-group">
                <label>Duração</label>
                <select
                  value={form.duration_days}
                  onChange={(event) => setForm((prev) => ({ ...prev, duration_days: parseInt(event.target.value, 10) }))}
                >
                  <option value={7}>7 dias</option>
                  <option value={14}>14 dias</option>
                  <option value={30}>30 dias</option>
                </select>
              </div>
            </div>

            <div className="input-group">
              <label>Público-alvo</label>
              <input
                value={form.target_audience}
                onChange={(event) => setForm((prev) => ({ ...prev, target_audience: event.target.value }))}
                placeholder="Ex: empreendedores 25-45 anos"
              />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={generateCampaign} disabled={generating}>
              {generating ? 'Gerando...' : <><Sparkles size={14} /> Gerar</>}
            </button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)} disabled={generating}>Cancelar</button>
          </div>
        </section>
      )}

      {loading && <HourglassLoader variant="inline" message="Carregando campanhas..." />}

      {!loading && (
        <section className="campaigns-grid">
          {campaigns.map((campaign) => (
            <article key={campaign.id} className="campaign-card card">
              <h3><Target size={16} /> {campaign.objective || 'Campanha'}</h3>
              <p>{campaign.summary || campaign.description || 'Campanha gerada por IA.'}</p>
              <div className="campaign-stats">
                <span>Plataforma: {campaign.platform || 'multi'}</span>
                <span>Duração: {campaign.duration_days || '-'} dias</span>
              </div>
            </article>
          ))}

          {!campaigns.length && (
            <article className="card empty-state">
              <h3>Sem campanhas ainda</h3>
              <p>Gere sua primeira campanha para começar.</p>
            </article>
          )}
        </section>
      )}
    </div>
  );
}

export default Campaigns;
